import { Card, Flex, Typography, SimpleTable, Dropdown } from "djuno-design";
import ExpandableCard from "../components/ExpandableCard";
import { GithubTypesLink } from "./../urls";

const { Title, Text } = Typography;

const DropdownPage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Dropdown</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The Dropdown component in Djuno Design provides customizable
            menu-based interactions in various UI types and styles. It supports
            default and simple dropdown types, offering flexibility in
            appearance and behavior.
          </Text>

          <Title level={5}>Variants and Customization</Title>
          <Text size="sm">
            The Dropdown component supports customization with anchor
            positioning:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Anchor Positioning
                </Text>
                : You can control the dropdown’s anchor positioning with the{" "}
                <Text code size="xs">
                  anchor
                </Text>{" "}
                prop. This allows flexibility in how the dropdown is aligned and
                displayed relative to its trigger element. Supported anchor
                positions include combinations of horizontal and vertical
                alignment such as "bottom start", "top end", etc.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Custom Styling
                </Text>
                : The dropdown allows for custom styling through{" "}
                <Text size="xs" code>
                  buttonClassName
                </Text>
                ,{" "}
                <Text size="xs" code>
                  itemsClassName
                </Text>
                , and{" "}
                <Text size="xs" code>
                  itemClassName
                </Text>{" "}
                props to match your design needs.
              </Text>
            </li>
          </ul>
        </Flex>

        {/* Examples */}
        <Flex direction="col">
          <Title level={5} className="mt-5">
            Examples
          </Title>
          <Flex direction={{ default: "col", lg: "row" }} className="gap-4">
            <ExpandableCard
              description="Default Dropdown"
              className="h-64"
              codeViewerProps={{
                code: DropdownExample1,
              }}
            >
              <Dropdown
                title="djuno Design"
                menu={[
                  { key: "1", label: "Edit" },
                  { type: "divider" },
                  { key: "end", label: "Delete", danger: true },
                ]}
                buttonClassName="cursor-pointer"
              >
                Click on Me!
              </Dropdown>
            </ExpandableCard>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable className="w-full">
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>

                <SimpleTable.Body>
                  {/* menu */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">menu</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The list of menu items to be shown in the dropdown.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "IDropdown.ts#L48"}
                        size="xs"
                        code
                      >
                        DropdownElement[]
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* title */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">title</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The text or title of the dropdown that is displayed to
                        the user.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"> </Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* buttonClassName */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">buttonClassName</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Custom class for the dropdown button.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* itemsClassName */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">itemsClassName</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Custom class for the dropdown items.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* itemClassName */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">itemClassName</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Custom class for the dropdown item.</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* anchor */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">anchor</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        the properties related to the anchor positioning or
                        alignment of UI component
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "IDropdown.ts#L67"}
                        size="xs"
                        code
                      >
                        AnchorProps
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* children */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">children</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">The content inside the dropdown</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        React.ReactNode
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">center</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const DropdownExample1 = `<Dropdown
  title="djuno Design"
  menu={[
    { key: "1", label: "Edit" },
    { type: "divider" },
    { key: "end", label: "Delete", danger: true },
  ]}
  buttonClassName="cursor-pointer"
>
  Djuno Design
</Dropdown>`;

export default DropdownPage;
